import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, Landmark, FileSignature } from 'lucide-react';
import { AmendmentNotice } from './AmendmentNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = 'bg-slate-800 border border-slate-700 rounded-2xl p-6';
const input = 'w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm focus:border-emerald-500 outline-none';
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const CreditStatement = ({ line, st, onOpen }) => {
  if (!line.agreement || st?.needs_resign) return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4" data-testid="credit-agreement-required">
      <p className="text-amber-300 text-sm mb-3">{st?.needs_resign ? 'Our credit agreement has been updated. Sign the new Subscriber Credit Agreement and Promissory Note to resume draws; your payment schedule is unchanged.' : 'Sign the 20-year Subscriber Credit Agreement and USD Promissory Note before your first draw: 1% interest-only monthly payments, repaid in BETH at 1 BETH = $1.00 USD.'}</p>
      <button onClick={onOpen} data-testid="credit-sign-agreement-btn" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-sm font-semibold flex items-center gap-2"><FileSignature className="w-4 h-4" /> Review &amp; sign agreement</button>
    </div>
  );
  if (st?.repayment_exempt) return (<>
    <AmendmentNotice a={st?.amendment} />
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4 text-sm text-emerald-300 flex items-center justify-between gap-3" data-testid="credit-repayment-exempt">
      <span>Administrator account: agreement signed; exempt from repayment, interest and automatic debits on any amount drawn.</span>
      <button onClick={onOpen} data-testid="credit-view-agreement-btn" className="underline shrink-0">View agreement</button>
    </div>
  </>);
  return (<>
    <AmendmentNotice a={st?.amendment} />
    <div className="bg-slate-900 rounded-lg p-4 mb-4 text-sm grid grid-cols-2 gap-2" data-testid="credit-statement">
      <p className="text-slate-400">Monthly payment: <span className="text-white" data-testid="credit-monthly-payment">{usd(st?.monthly_payment_usd)} ({(st?.monthly_payment_beth || 0).toLocaleString()} BETH)</span></p>
      <p className="text-slate-400">Next auto-debit: <span className="text-white" data-testid="credit-next-due">{st?.next_payment_due || '-'}</span></p>
      <p className="text-slate-400">Maturity: <span className="text-white">{st?.maturity_date}</span></p>
      <p className="text-slate-400">Past due: <span className={st?.past_due_usd ? 'text-red-400' : 'text-white'} data-testid="credit-past-due">{usd(st?.past_due_usd)}</span></p>
      <button onClick={onOpen} data-testid="credit-view-agreement-btn" className="col-span-2 text-left text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1"><FileSignature className="w-3 h-3" /> View signed agreement</button>
    </div>
  </>);
};

export const SubscriptionCard = ({ data, token, onChange }) => {
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const sub = data.subscription;

  const subscribe = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pas-banking/subscription/checkout`, {}, auth);
      window.location.href = res.data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start checkout'); setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try { await axios.post(`${API}/pas-banking/subscription/cancel`, {}, auth); toast.success('Subscription will end at the close of this billing period'); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Cancel failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className={card} data-testid="subscription-card">
      <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Award className="w-5 h-5 text-amber-400" /> Treasury Officer subscription</h3>
      {data.is_officer ? (
        <>
          <p className="text-amber-300 font-semibold mt-2" data-testid="officer-title">{data.title}</p>
          <p className="text-slate-400 text-sm mt-1">{sub.waived ? <span data-testid="subscription-waived">Fee waived by administrator</span> : '$499.00 / month'} &middot; status <span className="text-emerald-400" data-testid="subscription-status">{sub.status}</span>{sub.cancel_at_period_end ? ' (ends this period)' : ''}</p>
          {!sub.waived && !sub.cancel_at_period_end && <button onClick={cancel} disabled={busy} data-testid="cancel-subscription-btn" className="mt-4 text-sm text-slate-400 hover:text-red-400 underline">Cancel subscription</button>}
        </>
      ) : (
        <>
          <p className="text-slate-400 text-sm mb-4">$499.00/month, billed automatically. Grants the title <span className="text-white">BETH Exchange Market - Treasury Officer; Banker</span>, eligibility for a BETH line of credit up to $50,000,000, and transactions up to $250,000 per day.</p>
          <button onClick={subscribe} disabled={busy} data-testid="subscribe-btn" className="px-5 py-3 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-semibold disabled:opacity-50">{busy ? 'Redirecting...' : 'Subscribe - $499/month'}</button>
        </>
      )}
    </div>
  );
};

export const CreditLineCard = ({ data, token, onChange }) => {
  const navigate = useNavigate();
  const [req, setReq] = useState({ amount_usd: '', purpose: '' });
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const line = data.credit_line;

  const call = async (key, path, body) => {
    setBusy(key);
    try { const res = await axios.post(`${API}/pas-banking/credit-line/${path}`, body, auth); toast.success(res.data.message || 'Credit line request submitted'); setAmount(''); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Request failed'); }
    finally { setBusy(''); }
  };

  const canRequest = !line || ['rejected'].includes(line.status);

  return (
    <div className={card} data-testid="credit-line-card">
      <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Landmark className="w-5 h-5 text-cyan-400" /> BETH line of credit</h3>
      <p className="text-slate-400 text-sm mb-4">{line?.purpose === 'Administrator grant' ? 'Credit line granted by the BETHCOIN administrator.' : 'Up to $50,000,000, approved by the BETHCOIN administrator. Requires an active Treasury Officer subscription.'}</p>
      {line && (
        <div className="bg-slate-900 rounded-lg p-4 mb-4 text-sm" data-testid="credit-line-status">
          <p className="text-slate-400">Status: <span className={`font-semibold ${line.status === 'approved' ? 'text-emerald-400' : line.status === 'rejected' ? 'text-red-400' : 'text-amber-400'}`}>{line.status.toUpperCase()}</span></p>
          <p className="text-slate-400">Requested: <span className="text-white">{usd(line.requested_amount_usd)}</span></p>
          {line.status === 'approved' && (
            <>
              <p className="text-slate-400">Approved limit: <span className="text-white" data-testid="credit-limit">{usd(line.approved_amount_usd)}</span></p>
              <p className="text-slate-400">Drawn: <span className="text-white" data-testid="credit-used">{usd(line.used_usd)}</span> &middot; Available: <span className="text-emerald-400" data-testid="credit-available">{usd(line.approved_amount_usd - line.used_usd)}</span></p>
            </>
          )}
          {line.note && <p className="text-slate-500 mt-1">Note: {line.note}</p>}
        </div>
      )}
      {line?.status === 'approved' && <CreditStatement line={line} st={data.credit_statement} onOpen={() => navigate('/pas/credit-agreement')} />}
      {line?.status === 'approved' && line.agreement && (
        <div className="flex gap-2">
          <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="USD amount" className={input} data-testid="credit-amount-usd-input" />
          <button onClick={() => call('draw', 'draw', { usd_amount: parseFloat(amount) })} disabled={!amount || busy || data.credit_statement?.needs_resign} data-testid="credit-draw-btn" className="px-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">Draw</button>
          <button onClick={() => call('repay', 'repay', { usd_amount: parseFloat(amount) })} disabled={!amount || busy} data-testid="credit-repay-btn" className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50">Repay</button>
        </div>
      )}
      {line?.status === 'approved' && line.agreement && amount > 0 && <p className="text-xs text-slate-500 mt-2" data-testid="credit-repay-beth-preview">Repaying ${amount} costs {parseFloat(amount).toLocaleString()} BETH (1 BETH = $1.00 USD)</p>}
      {canRequest && (
        <form onSubmit={(e) => { e.preventDefault(); call('request', 'request', { amount_usd: parseFloat(req.amount_usd), purpose: req.purpose }); }} className="space-y-3">
          <input type="number" min="1" max="50000000" step="0.01" required value={req.amount_usd} onChange={(e) => setReq({ ...req, amount_usd: e.target.value })} placeholder="Requested amount (USD, max 50,000,000)" className={input} data-testid="credit-request-amount-input" />
          <input required value={req.purpose} onChange={(e) => setReq({ ...req, purpose: e.target.value })} placeholder="Purpose of the credit line" className={input} data-testid="credit-request-purpose-input" />
          <button type="submit" disabled={!data.is_officer || busy === 'request'} data-testid="credit-request-btn" className="px-5 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium disabled:opacity-50">Request credit line</button>
          {!data.is_officer && <p className="text-xs text-amber-400">Subscribe as a Treasury Officer to request a credit line.</p>}
        </form>
      )}
    </div>
  );
};
